import React, { useState, useEffect } from 'react';
import {
  X, FileText, Download, Send, Copy, ExternalLink,
  Building2, User, Calendar, Shield, Globe, Clock,
  CheckCircle, AlertCircle, Eye, Loader2, Archive
} from 'lucide-react';
import { toast } from '../../../utils/toastUtils';
import { contracts as contractsApi } from '../../../services/api/client';
import api from '../../../services/api/client';

const ContractPreviewDialog = ({ contract, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [loadingProof, setLoadingProof] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const [activeTab, setActiveTab] = useState('details');
  const [providerInfo, setProviderInfo] = useState(null);
  const [contractTemplate, setContractTemplate] = useState(null);

  // Load provider info from account if contract provider info is empty
  useEffect(() => {
    const loadProviderInfo = async () => {
      try {
        const response = await api.get('/account/profile');
        const profile = response.data?.data || response.data;
        if (profile) {
          const company = profile.company || {};
          setProviderInfo({
            name: company.name || 'SYLink Security',
            legal_form: company.legal_form || '',
            address: company.address || '',
            postal_code: company.postal_code || '',
            city: company.city || '',
            siret: company.siret || '',
            siren: company.siren || '',
            representative_name: profile.full_name || profile.username || '',
            representative_function: profile.job_title || 'Pentester',
            contact_email: profile.email || company.contact_email || '',
            contact_phone: profile.phone || company.contact_phone || ''
          });
        }
      } catch (error) {
        console.error('Error loading provider info:', error);
      }
    };
    loadProviderInfo();
  }, []);

  // Load contract template from account settings
  useEffect(() => {
    const loadTemplate = async () => {
      try {
        const response = await api.get('/account/contract-templates');
        const templates = response.data || [];
        // Find default template or first one
        const defaultTemplate = templates.find(t => t.is_default) || templates.find(t => t.code === 'pentest_authorization') || templates[0];
        if (defaultTemplate) {
          setContractTemplate(defaultTemplate);
        }
      } catch (error) {
        console.error('Error loading contract template:', error);
      }
    };
    loadTemplate();
  }, []);

  // Get provider value (from contract or fallback to account)
  const getProviderValue = (field) => {
    const contractField = `provider_${field}`;
    if (contract?.[contractField]) return contract[contractField];
    if (providerInfo?.[field]) return providerInfo[field];
    return null;
  };

  // Generate perimeter table HTML
  const generatePerimeterTable = () => {
    if (!Array.isArray(contract?.perimeter) || contract.perimeter.length === 0) {
      return '<p><em>Périmètre non défini</em></p>';
    }

    let html = `<table style="width: 100%; border-collapse: collapse; margin: 10px 0;">
      <thead>
        <tr style="background-color: #f5f5f5;">
          <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Réf.</th>
          <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Type</th>
          <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Détail</th>
          <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Environnement</th>
          <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Commentaire</th>
        </tr>
      </thead>
      <tbody>`;

    contract.perimeter.forEach(item => {
      html += `<tr>
        <td style="border: 1px solid #ddd; padding: 8px;">${item.ref || ''}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${item.type || ''}</td>
        <td style="border: 1px solid #ddd; padding: 8px; font-family: monospace; font-size: 12px;">${item.detail || ''}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${item.env || ''}</td>
        <td style="border: 1px solid #ddd; padding: 8px;">${item.comment || '-'}</td>
      </tr>`;
    });

    html += '</tbody></table>';
    return html;
  };

  // Generate preview from template with variable substitution
  const generatePreviewFromTemplate = () => {
    if (!contractTemplate?.content) return '';

    let content = contractTemplate.content;

    // Format dates
    const formatDate = (dateStr) => {
      if (!dateStr) return '_______________';
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: 'numeric', month: 'long', year: 'numeric'
      });
    };

    // Variable substitutions
    const variables = {
      // Prestataire
      '{{prestataire_nom}}': getProviderValue('name') || '_______________',
      '{{prestataire_forme}}': getProviderValue('legal_form') || '_______________',
      '{{prestataire_capital}}': '_______________',
      '{{prestataire_siege}}': [getProviderValue('address'), getProviderValue('postal_code'), getProviderValue('city')].filter(Boolean).join(', ') || '_______________',
      '{{prestataire_rcs}}': '_______________',
      '{{prestataire_representant}}': getProviderValue('representative_name') || '_______________',
      '{{prestataire_fonction}}': getProviderValue('representative_function') || '_______________',

      // Client
      '{{client_nom}}': contract?.client_name || '_______________',
      '{{client_forme}}': contract?.client_legal_form || '_______________',
      '{{client_capital}}': '_______________',
      '{{client_siege}}': [contract?.client_address, contract?.client_postal_code, contract?.client_city].filter(Boolean).join(', ') || '_______________',
      '{{client_rcs}}': '_______________',
      '{{client_siren}}': contract?.client_siret || '_______________',
      '{{client_representant}}': contract?.client_representative_name || '_______________',
      '{{client_fonction}}': contract?.client_representative_function || '_______________',
      '{{client_email}}': contract?.client_contact_email || '_______________',
      '{{client_telephone}}': contract?.client_contact_phone || '_______________',

      // Périmètre et dates
      '{{perimetre_tableau}}': generatePerimeterTable(),
      '{{date_debut}}': formatDate(contract?.start_date),
      '{{date_fin}}': formatDate(contract?.end_date),
      '{{date_signature}}': formatDate(new Date().toISOString()),
      '{{duree_risque}}': '30',

      // Ancien format (compatibilité)
      '{{company_name}}': getProviderValue('name') || '_______________',
      '{{scan_target}}': Array.isArray(contract?.perimeter) ? contract.perimeter.map(p => p.detail).join(', ') : '_______________',
      '{{current_date}}': formatDate(new Date().toISOString()),
      '{{signatory_name}}': contract?.client_representative_name || '_______________',
      '{{signatory_email}}': contract?.client_contact_email || '_______________',
    };

    // Apply substitutions
    Object.entries(variables).forEach(([key, value]) => {
      content = content.split(key).join(value);
    });

    // Convert plain text to HTML with proper formatting
    const htmlContent = content
      .split('\n\n').map(paragraph => {
        // Check if it's a heading
        if (paragraph.startsWith('ARTICLE') || paragraph.startsWith('ENTRE') || paragraph.startsWith('PREAMBULE') || paragraph.startsWith('IL A ETE')) {
          return `<h3 style="margin-top: 20px; margin-bottom: 10px; font-weight: bold;">${paragraph}</h3>`;
        }
        if (paragraph.startsWith('CONTRAT')) {
          return `<h1 style="text-align: center; margin-bottom: 20px; font-size: 18px; font-weight: bold;">${paragraph}</h1>`;
        }
        // Check for lists
        if (paragraph.includes('\n- ')) {
          const lines = paragraph.split('\n');
          const firstLine = lines[0];
          const listItems = lines.slice(1).filter(l => l.startsWith('- ')).map(l => `<li>${l.substring(2)}</li>`).join('');
          return `<p>${firstLine}</p><ul style="margin-left: 20px;">${listItems}</ul>`;
        }
        return `<p style="margin-bottom: 10px;">${paragraph.replace(/\n/g, '<br/>')}</p>`;
      }).join('');

    return htmlContent;
  };

  // Generate HTML content from template
  useEffect(() => {
    if (contract?.generated_content) {
      setHtmlContent(contract.generated_content);
    } else if (contractTemplate && providerInfo && contract) {
      // Generate preview from template
      const generatedHtml = generatePreviewFromTemplate();
      setHtmlContent(generatedHtml);
    }
  }, [contract, contractTemplate, providerInfo]);

  // Status config
  const statusConfig = {
    draft: { label: 'Brouillon', color: 'bg-gray-500', textColor: 'text-gray-300' },
    pending_signature: { label: 'En attente de signature', color: 'bg-yellow-500', textColor: 'text-yellow-300' },
    signed: { label: 'Signé', color: 'bg-green-500', textColor: 'text-green-300' },
    active: { label: 'Actif', color: 'bg-blue-500', textColor: 'text-blue-300' },
    completed: { label: 'Terminé', color: 'bg-purple-500', textColor: 'text-purple-300' },
    expired: { label: 'Expiré', color: 'bg-red-500', textColor: 'text-red-300' },
    cancelled: { label: 'Annulé', color: 'bg-gray-600', textColor: 'text-gray-400' }
  };

  const status = statusConfig[contract?.status] || statusConfig.draft;

  // Build full signature URL (handles both legacy relative paths and new absolute URLs)
  const getFullSignatureUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${window.location.origin}${url}`;
  };

  // Copy signature link
  const copySignatureLink = () => {
    if (contract?.signature_url) {
      navigator.clipboard.writeText(getFullSignatureUrl(contract.signature_url));
      toast.success('Lien de signature copié');
    }
  };

  // Download PDF
  const handleDownloadPdf = async () => {
    setLoading(true);
    try {
      const blob = await contractsApi.downloadPdf(contract.id);

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contrat-${contract.contract_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors du téléchargement du PDF');
    } finally {
      setLoading(false);
    }
  };

  // Download signature proof (ZIP)
  const handleDownloadSignatureProof = async () => {
    setLoadingProof(true);
    try {
      const blob = await contractsApi.downloadSignatureProof(contract.id);

      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `preuve-signature-${contract.contract_number}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Preuve de signature téléchargée');
    } catch (error) {
      toast.error('Erreur lors du téléchargement de la preuve');
    } finally {
      setLoadingProof(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-brand-black-light rounded-xl border border-brand-green/30 w-full max-w-6xl max-h-[95vh] overflow-hidden flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="p-2 bg-brand-green/20 rounded-lg flex-shrink-0">
              <FileText className="w-6 h-6 text-brand-green" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-white truncate" title={contract?.title}>{contract?.title}</h2>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-400">{contract?.contract_number}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs ${status.color} text-white flex-shrink-0`}>
                  {status.label}
                </span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-2 hover:bg-gray-700 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-700">
          <button
            onClick={() => setActiveTab('details')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'details'
                ? 'text-brand-green border-brand-green'
                : 'text-gray-400 border-transparent hover:text-white'
            }`}
          >
            Détails
          </button>
          <button
            onClick={() => setActiveTab('perimeter')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'perimeter'
                ? 'text-brand-green border-brand-green'
                : 'text-gray-400 border-transparent hover:text-white'
            }`}
          >
            Périmètre
          </button>
          <button
            onClick={() => setActiveTab('preview')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'preview'
                ? 'text-brand-green border-brand-green'
                : 'text-gray-400 border-transparent hover:text-white'
            }`}
          >
            Aperçu du contrat
          </button>
          <button
            onClick={() => setActiveTab('signature')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'signature'
                ? 'text-brand-green border-brand-green'
                : 'text-gray-400 border-transparent hover:text-white'
            }`}
          >
            Signature
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'details' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Provider */}
              <div className="bg-gray-700/50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-400 mb-4 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Prestataire
                </h3>
                <div className="space-y-3">
                  <p className="text-white font-medium">{getProviderValue('name')}</p>
                  {getProviderValue('legal_form') && (
                    <p className="text-gray-400 text-sm">{getProviderValue('legal_form')}</p>
                  )}
                  {getProviderValue('address') && (
                    <p className="text-gray-400 text-sm">
                      {getProviderValue('address')}<br />
                      {getProviderValue('postal_code')} {getProviderValue('city')}
                    </p>
                  )}
                  {getProviderValue('siret') && (
                    <p className="text-gray-400 text-sm">SIRET: {getProviderValue('siret')}</p>
                  )}
                  {getProviderValue('siren') && (
                    <p className="text-gray-400 text-sm">SIREN: {getProviderValue('siren')}</p>
                  )}
                  <div className="pt-2 border-t border-gray-600">
                    <p className="text-white text-sm">{getProviderValue('representative_name')}</p>
                    <p className="text-gray-400 text-xs">{getProviderValue('representative_function')}</p>
                    {getProviderValue('contact_email') && (
                      <p className="text-gray-400 text-xs mt-1">{getProviderValue('contact_email')}</p>
                    )}
                    {getProviderValue('contact_phone') && (
                      <p className="text-gray-400 text-xs">{getProviderValue('contact_phone')}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Client */}
              <div className="bg-gray-700/50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-400 mb-4 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Client
                </h3>
                <div className="space-y-3">
                  <p className="text-white font-medium capitalize">{contract?.client_name}</p>
                  {contract?.client_legal_form && (
                    <p className="text-gray-400 text-sm">{contract.client_legal_form}</p>
                  )}
                  {contract?.client_address && (
                    <p className="text-gray-400 text-sm">
                      {contract.client_address}<br />
                      {contract.client_postal_code} {contract.client_city}
                    </p>
                  )}
                  {contract?.client_siret && (
                    <p className="text-gray-400 text-sm">SIRET: {contract.client_siret}</p>
                  )}
                  <div className="pt-2 border-t border-gray-600">
                    <p className="text-white text-sm">{contract?.client_representative_name}</p>
                    <p className="text-gray-400 text-xs">{contract?.client_representative_function}</p>
                    <p className="text-gray-400 text-xs mt-1">{contract?.client_contact_email}</p>
                    <p className="text-gray-400 text-xs">{contract?.client_contact_phone}</p>
                  </div>
                </div>
              </div>

              {/* Period */}
              <div className="bg-gray-700/50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-400 mb-4 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Période d'intervention
                </h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-gray-400 text-sm">Début:</span>
                    <p className="text-white">{formatDate(contract?.start_date)}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm">Fin:</span>
                    <p className="text-white">{formatDate(contract?.end_date)}</p>
                  </div>
                  {contract?.time_slots && (
                    <div>
                      <span className="text-gray-400 text-sm">Plages horaires:</span>
                      <p className="text-white">{contract.time_slots}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Dates */}
              <div className="bg-gray-700/50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-400 mb-4 flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Historique
                </h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-gray-400 text-sm">Créé le:</span>
                    <p className="text-white">{formatDateTime(contract?.created_at)}</p>
                  </div>
                  {contract?.sent_at && (
                    <div>
                      <span className="text-gray-400 text-sm">Envoyé le:</span>
                      <p className="text-white">{formatDateTime(contract.sent_at)}</p>
                    </div>
                  )}
                  {contract?.viewed_at && (
                    <div>
                      <span className="text-gray-400 text-sm">Consulté le:</span>
                      <p className="text-white">{formatDateTime(contract.viewed_at)}</p>
                    </div>
                  )}
                  {contract?.signed_at && (
                    <div>
                      <span className="text-gray-400 text-sm">Signé le:</span>
                      <p className="text-green-400">{formatDateTime(contract.signed_at)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'perimeter' && (
            <div className="space-y-6">
              {/* Périmètre en format tableau */}
              {Array.isArray(contract?.perimeter) && contract.perimeter.length > 0 ? (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-400 mb-4 flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Périmètre des tests
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-400 border-b border-gray-600">
                          <th className="text-left py-2 px-3 font-medium">Réf.</th>
                          <th className="text-left py-2 px-3 font-medium">Type</th>
                          <th className="text-left py-2 px-3 font-medium">Détail</th>
                          <th className="text-left py-2 px-3 font-medium">Environnement</th>
                          <th className="text-left py-2 px-3 font-medium">Commentaire</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contract.perimeter.map((item, idx) => (
                          <tr key={idx} className="border-b border-gray-600/50 hover:bg-gray-600/30">
                            <td className="py-2 px-3 text-brand-green font-medium">{item.ref}</td>
                            <td className="py-2 px-3">
                              <span className="px-2 py-1 bg-blue-500/20 text-blue-300 rounded text-xs">
                                {item.type}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-white font-mono text-xs">{item.detail}</td>
                            <td className="py-2 px-3">
                              <span className={`px-2 py-1 rounded text-xs ${
                                item.env === 'Production' ? 'bg-red-500/20 text-red-300' :
                                item.env === 'Test' ? 'bg-yellow-500/20 text-yellow-300' :
                                'bg-gray-500/20 text-gray-300'
                              }`}>
                                {item.env}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-gray-400 text-xs">{item.comment || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                /* Format alternatif (ancien format) */
                contract?.perimeter?.test_types || contract?.perimeter?.ip_ranges || contract?.perimeter?.domains ? (
                  <>
                    {/* Test types */}
                    {contract.perimeter.test_types?.length > 0 && (
                      <div className="bg-gray-700/50 rounded-lg p-4">
                        <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                          <Shield className="w-4 h-4" />
                          Types de tests autorisés
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {contract.perimeter.test_types.map((type) => (
                            <span
                              key={type}
                              className="px-3 py-1.5 bg-brand-green/20 text-brand-green rounded-lg text-sm"
                            >
                              {type}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* IP Ranges */}
                    {contract.perimeter.ip_ranges?.length > 0 && (
                      <div className="bg-gray-700/50 rounded-lg p-4">
                        <h3 className="text-sm font-medium text-gray-400 mb-3">Plages IP / CIDR</h3>
                        <div className="space-y-1">
                          {contract.perimeter.ip_ranges.map((ip, index) => (
                            <div key={index} className="flex items-center gap-2 text-white font-mono text-sm">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              {ip}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Domains */}
                    {contract.perimeter.domains?.length > 0 && (
                      <div className="bg-gray-700/50 rounded-lg p-4">
                        <h3 className="text-sm font-medium text-gray-400 mb-3 flex items-center gap-2">
                          <Globe className="w-4 h-4" />
                          Domaines
                        </h3>
                        <div className="space-y-1">
                          {contract.perimeter.domains.map((domain, index) => (
                            <div key={index} className="flex items-center gap-2 text-white text-sm">
                              <CheckCircle className="w-4 h-4 text-green-500" />
                              {domain}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Exclusions */}
                    {contract.perimeter.exclusions?.length > 0 && (
                      <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
                        <h3 className="text-sm font-medium text-red-400 mb-3 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          Exclusions (hors périmètre)
                        </h3>
                        <div className="space-y-1">
                          {contract.perimeter.exclusions.map((exclusion, index) => (
                            <div key={index} className="flex items-center gap-2 text-red-300 text-sm">
                              <X className="w-4 h-4" />
                              {exclusion}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center justify-center h-32 text-gray-400">
                    <div className="text-center">
                      <Shield className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>Aucun périmètre défini</p>
                    </div>
                  </div>
                )
              )}
            </div>
          )}

          {activeTab === 'preview' && (
            <div className="bg-white rounded-lg p-6 min-h-[400px] max-h-[60vh] overflow-y-auto">
              {htmlContent ? (
                <div
                  className="prose prose-sm max-w-none text-gray-900"
                  style={{ fontFamily: 'serif' }}
                  dangerouslySetInnerHTML={{ __html: htmlContent }}
                />
              ) : !contractTemplate && !providerInfo ? (
                /* Chargement du template */
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-gray-400" />
                    <p className="text-gray-500">Chargement du modèle de contrat...</p>
                  </div>
                </div>
              ) : (
                /* Aucun template configuré */
                <div className="flex items-center justify-center h-32">
                  <div className="text-center">
                    <FileText className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-500">Aucun modèle de contrat configuré.</p>
                    <p className="text-gray-400 text-sm">Configurez un modèle dans Compte → Contrats</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'signature' && (
            <div className="space-y-6">
              {/* Signature status */}
              <div className={`p-4 rounded-lg ${
                contract?.status === 'signed' || contract?.status === 'active' || contract?.status === 'completed'
                  ? 'bg-green-900/30 border border-green-700'
                  : contract?.status === 'pending_signature'
                  ? 'bg-yellow-900/30 border border-yellow-700'
                  : 'bg-gray-700/50'
              }`}>
                <h3 className="text-sm font-medium text-gray-300 mb-2">Statut de la signature</h3>
                <p className={`text-lg font-medium ${status.textColor}`}>
                  {status.label}
                </p>

                {contract?.signed_at && (
                  <div className="mt-3 pt-3 border-t border-gray-600">
                    <p className="text-gray-400 text-sm">Signé le {formatDateTime(contract.signed_at)}</p>
                    {contract?.signature_ip && (
                      <p className="text-gray-500 text-xs mt-1">IP : {contract.signature_ip}</p>
                    )}
                    {contract?.signature_text && (
                      <p className="text-gray-400 text-sm mt-2">
                        Nom saisi: <span className="text-white">{contract.signature_text}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Signature link */}
              {contract?.signature_url && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-400 mb-3">Lien de signature</h3>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={getFullSignatureUrl(contract.signature_url)}
                      readOnly
                      className="flex-1 px-3 py-2 bg-brand-black border border-gray-600 rounded-lg text-white text-sm"
                    />
                    <button
                      onClick={copySignatureLink}
                      className="p-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
                      title="Copier le lien"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <a
                      href={getFullSignatureUrl(contract.signature_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 bg-brand-green hover:bg-brand-green-light text-black rounded-lg transition-colors"
                      title="Ouvrir le lien"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              )}

              {/* View count */}
              {contract?.view_count > 0 && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-400 mb-2 flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Statistiques de consultation
                  </h3>
                  <p className="text-white">
                    {contract.view_count} consultation{contract.view_count > 1 ? 's' : ''}
                  </p>
                  {contract?.viewed_at && (
                    <p className="text-gray-400 text-sm">
                      Dernière consultation: {formatDateTime(contract.viewed_at)}
                    </p>
                  )}
                </div>
              )}

              {/* Signature data (if signed) */}
              {contract?.signature_data && (
                <div className="bg-gray-700/50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-gray-400 mb-3">Signature</h3>
                  <div className="bg-white rounded-lg p-4">
                    <img
                      src={contract.signature_data}
                      alt="Signature"
                      className="max-h-32 mx-auto"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-700">
          {/* Bouton preuve de signature - visible uniquement pour les contrats signés */}
          {(contract?.status === 'signed' || contract?.status === 'active' || contract?.status === 'completed') && (
            <button
              onClick={handleDownloadSignatureProof}
              disabled={loadingProof}
              className="flex items-center gap-2 px-4 py-2 bg-green-700 hover:bg-green-600 text-white rounded-lg disabled:opacity-50 transition-colors"
              title="Télécharger l'archive ZIP contenant toutes les preuves de signature"
            >
              {loadingProof ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Archive className="w-4 h-4" />
              )}
              Preuve de signature
            </button>
          )}
          {contract?.status === 'signed' && (
            <button
              onClick={handleDownloadPdf}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg disabled:opacity-50 transition-colors"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Télécharger PDF
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContractPreviewDialog;
